import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
} from '@mui/material';
import { HeadCell, TableHeader } from '../../components/formlib/TableHeader';
import {
  Appointment,
  GroupAppointment,
} from '../../services/configApi/activitySheet/weeklyActivitySheetProvider';
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import MediumTypography from '../../components/formlib/MediumTypography';
import { getAppStartTime } from '../../utils/dateUtil';
import { ReactComponent as GroupIcon } from '../../assets/images/groupIcon.svg';
import { ReactComponent as SingleIcon } from '../../assets/images/singleIcon.svg';
import { ReactComponent as UndoIcon } from '../../assets/images/undoIcon.svg';
import { ReactComponent as ProgressNote } from '../../assets/images/ProgressNoteIcon.svg';
import { ReactComponent as ArchiveIcon } from '../../assets/images/archiveIcon.svg';
import { ReactComponent as StatusIcon } from '../../assets/images/statusIcon.svg';
import TextWithIcon from '../../components/formlib/TextWithIcon';
import { ReactComponent as MessageIcon } from '../../assets/images/messageIcon.svg';

import {
  LOCAL_DATE_TIME_UTC_FORMAT,
  LOCAL_TIME_12_HOUR_FORMAT,
} from '../../services/Constant';
import ActionToolTip from '../../components/formlib/ActionToolTip';
import { StatusType } from './ActivityTableRow';
import { formatName } from '../../utils/nameUtils';
import { useNavigate } from 'react-router';
import { PROGRESS_NOTE_SETUP } from '../../routes/Routing';
import { checkPermissionForFeature } from '../../utils/checkPermission';
import { useIntl } from 'react-intl';
import { LookUpContext } from '../../context/LookUpContextProvider';
import { LookupItem } from '../../services/configApi/tcsLookups';
import dayjs from 'dayjs';
import { formatNumberToDecimal, getStatusColor } from '../../utils/utilities';

interface Header {
  empty: string;
  clientName: string;
  apptStartTime: string;
  duration: string;
  serviceCode: string;
  settingCode: string;
  location: string;
  modifiers: string;
  status: string;
  actions: string;
}

const headCells: HeadCell<Header>[] = [
  {
    id: 'empty',
    labelId: '',
    defaultLabelId: '',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'clientName',
    labelId: 'Dashboard.ClientName',
    defaultLabelId: 'Client Name',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'apptStartTime',
    labelId: 'appt',
    defaultLabelId: 'Appt Start Time',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'duration',
    labelId: 'myTimeOffDuration',
    defaultLabelId: 'Duration',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'serviceCode',
    labelId: 'serviceCodeText',
    defaultLabelId: 'Service Code',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'settingCode',
    labelId: 'settingCodeText',
    defaultLabelId: 'Setting Code',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'location',
    labelId: 'locationText',
    defaultLabelId: 'Location',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'modifiers',
    labelId: 'modifiersText',
    defaultLabelId: 'Modifiers',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'status',
    labelId: 'statusText',
    defaultLabelId: 'Status',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'actions',
    labelId: 'actions',
    defaultLabelId: 'Actions',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'empty',
    labelId: '',
    defaultLabelId: '',
    numeric: false,
    requiredSorting: false,
  },
];

const AppointmentTableRow: FC<{
  groupAppointments: GroupAppointment[];
  parentArchived: boolean;
  isHoliday: boolean;
  rowId: number;
  isEmployee: boolean;
  parentStatus: StatusType | null;
  archiveAppointment: (id: string, archived: boolean) => void;
  updateVisitNote: (
    progressNoteId: string,
    visitId: string,
    description: string | null,
    visitStatus: StatusType | null,
  ) => void;
  screenName: string;
  selectedDate?: Date;
}> = ({
  groupAppointments,
  parentArchived,
  archiveAppointment,
  isEmployee,
  isHoliday,
  rowId,
  parentStatus,
  screenName,
  selectedDate,
  updateVisitNote,
}) => {
  const navigate = useNavigate();
  const [serviceCode, setServiceCode] = useState<LookupItem[]>([]);
  const [settingCode, setSettingCode] = useState<LookupItem[]>([]);
  const [clientLocations, setClientLocations] = useState<LookupItem[]>([]);
  const rctl = useIntl();
  const { lookups } = useContext(LookUpContext);

  useEffect(() => {
    if (lookups === null) {
      return;
    }

    setServiceCode(() => {
      return lookups.serviceCode.map((e) => {
        return e;
      });
    });

    setSettingCode(() => {
      return lookups.settingsCode.map((e) => {
        return e;
      });
    });

    setClientLocations(() => {
      return lookups.location.map((e) => {
        return e;
      });
    });
  }, [lookups]);

  const getServiceCode = useCallback(
    (id: string | null) => {
      if (id === null) return '-';
      const code = serviceCode.find(
        (item) => item.code === id || item.id === id,
      );
      return code ? code.code : '-';
    },
    [serviceCode],
  );

  const getSettingsCode = useCallback(
    (id: string | null) => {
      if (id === null) return '-';
      const code = settingCode.find(
        (item) => item.code === id || item.id === id,
      );
      return code ? code.code : '-';
    },
    [settingCode],
  );

  const getClientLocation = useCallback(
    (clientLocation: string | null) => {
      if (clientLocation === null || clientLocation.length === 0) return '-';
      const code = clientLocations.find((item) => item.code === clientLocation);
      return code ? code.description : '-';
    },
    [clientLocations],
  );

  const getFormatedDurationInDecimal = useCallback(
    (duration: number | null) =>
      `${formatNumberToDecimal(duration) || '0.00'} hrs`,
    [],
  );

  const getBackgroundColor = () => {
    if (isHoliday) {
      return '#FFEBDD';
    } else if (parentArchived) {
      return '#97A6A5';
    } else if (rowId === 0) {
      return '#ffffff';
    } else {
      return '#ECF9F8';
    }
  };

  const getClassName = () => {
    if (isHoliday) {
      return 'activity-table-bg holidayBg listDataTableHead';
    }
    return `activity-table-bg ${
      rowId !== 0 ? 'activitybg' : 'activitytransbg'
    } + ' listDataTableHead `;
  };

  const renderStatus = (status: StatusType | null) => (
    <Box sx={{ display: 'flex' }} alignItems={'center'}>
      <StatusIcon fill={getStatusColor(status ?? 'Not Submitted')} />
      <MediumTypography
        className="singleLine"
        sxProps={{ paddingLeft: '8px' }}
        label={status ?? 'Not Submitted'}
      />
    </Box>
  );

  const getActivityTableRow = (
    appointment: Appointment,
    isGroupBilling: boolean,
    index: number,
    removeBorder: boolean,
    hasInterpreter: boolean,
  ) => {
    return (
      <>
        {index === 0 && isGroupBilling && !hasInterpreter && (
          <TableRow>
            <TableCell
              sx={{
                padding: '0px 8px 0px 0px !important',
                backgroundColor: getBackgroundColor(),
                borderBottom: 'none',
                borderLeft: '1px solid #00C6B8',
              }}
            >
              <Box display={'flex'} alignItems={'center'}>
                <Box
                  sx={{
                    width: '20px',
                    backgroundColor: '#00C6B8',
                    height: '1px',
                  }}
                />
                <GroupIcon />
              </Box>
            </TableCell>
            <TableCell
              colSpan={9}
              sx={{
                borderBottom: isGroupBilling ? 'unset' : '#CFDEDE',
                maxWidth: '150px',
              }}
            >
              <Box className={'flex__'}>
                <MediumTypography
                  className="mr-sm"
                  label={`${rctl.formatMessage({
                    id: 'groupBillingText',
                    defaultMessage: 'Clinician Group Billable Hours',
                  })}:`}
                  sxProps={{ fontWeight: 'bold' }}
                />

                <Box sx={{ display: 'flex' }}>
                  <MediumTypography
                    className="mr-xs"
                    label={`${rctl.formatMessage({
                      id: 'startTimeText',
                      defaultMessage: 'Start Time',
                    })} - `}
                  />
                  <MediumTypography
                    className="mr-sm"
                    label={`${
                      appointment.groupBillingStartTime === null
                        ? '00:00'
                        : dayjs(
                            appointment.groupBillingStartTime,
                            LOCAL_DATE_TIME_UTC_FORMAT,
                          ).format('hh:mm A')
                    };`}
                  />
                </Box>

                <Box sx={{ display: 'flex' }}>
                  <MediumTypography
                    className="mr-xs"
                    label={`${rctl.formatMessage({
                      id: 'ProgressNote.duration',
                      defaultMessage: 'Duration',
                    })} - `}
                  />
                  <MediumTypography
                    className="mr-sm"
                    label={getFormatedDurationInDecimal(
                      appointment.groupBillingDurationDec,
                    )}
                  />
                </Box>
              </Box>
            </TableCell>
          </TableRow>
        )}
        <TableRow
          key={appointment.appointmentId}
          sx={{
            backgroundColor: appointment.onHold ? '#D5D5D5' : '',
          }}
        >
          <TableCell
            sx={{
              padding: '0px 8px 0px 0px !important',
              borderBottom: isGroupBilling ? 'unset' : '#CFDEDE',
              backgroundColor: getBackgroundColor(),
              borderLeft: appointment.isLastIndex ? '' : '1px solid #00C6B8',
              position: 'relative',
              '&::before': {
                display: appointment.isLastIndex ? 'block' : 'none',
                content: '""',
                position: 'absolute',
                left: -0.5,
                top: '0%', // Start 25% from the top to center the border vertically
                height: '50%', // 50% height of the cell
                width: '1px', // Border thickness
                backgroundColor: '#00C6B8', // Border color
              },
            }}
          >
            {!isGroupBilling && (
              <Box display={'flex'} alignItems={'center'}>
                <Box
                  sx={{
                    width: '20px',
                    backgroundColor: '#00C6B8',
                    height: '1px',
                  }}
                />
                <SingleIcon />
              </Box>
            )}
          </TableCell>
          <TableCell
            className={`${
              removeBorder ? 'disableBorderBotton' : 'enableBorderBottom'
            }`}
          >
            <Tooltip
              id="tool-tip"
              title={
                formatName(
                  appointment.clientFirstName,
                  appointment.clientMiddleName,
                  appointment.clientLastName,
                  appointment.clientSuffix ?? '',
                ).length > 22
                  ? formatName(
                      appointment.clientFirstName,
                      appointment.clientMiddleName,
                      appointment.clientLastName,
                      appointment.clientSuffix ?? '',
                    )
                  : ''
              }
              placement="bottom"
            >
              <Box>
                <MediumTypography
                  label={formatName(
                    appointment.clientFirstName,
                    appointment.clientMiddleName,
                    appointment.clientLastName,
                    appointment.clientSuffix ?? '',
                  )}
                  sxProps={{
                    color: '#008C82',
                    marginLeft: '8px',
                    maxWidth: '150px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                />
              </Box>
            </Tooltip>
          </TableCell>
          <TableCell
            padding={'none'}
            className={`${
              removeBorder ? 'disableBorderBotton' : 'enableBorderBottom'
            }`}
          >
            <MediumTypography
              label={getAppStartTime(
                appointment.startTime,
                LOCAL_TIME_12_HOUR_FORMAT,
                true,
              )}
              sxProps={{ color: '#008C82', marginRight: '4px' }}
            />
          </TableCell>
          <TableCell
            padding={'none'}
            className={`${
              removeBorder ? 'disableBorderBotton' : 'enableBorderBottom'
            }`}
          >
            <MediumTypography
              label={
                appointment.attendeeRole !== 'CLINICIAN'
                  ? '-'
                  : getFormatedDurationInDecimal(appointment.sessionDurationDec)
              }
            />
          </TableCell>
          <TableCell
            padding={'none'}
            className={`${
              removeBorder ? 'disableBorderBotton' : 'enableBorderBottom'
            }`}
          >
            <MediumTypography label={getServiceCode(appointment.serviceCode)} />
          </TableCell>
          <TableCell
            padding={'none'}
            className={`${
              removeBorder ? 'disableBorderBotton' : 'enableBorderBottom'
            }`}
          >
            <MediumTypography
              label={getSettingsCode(appointment.settingCode)}
            />
          </TableCell>
          <TableCell
            padding={'none'}
            className={`${
              removeBorder ? 'disableBorderBotton' : 'enableBorderBottom'
            }`}
          >
            <MediumTypography
              label={getClientLocation(appointment.clientLocationType)}
            />
          </TableCell>
          <TableCell
            padding={'none'}
            className={`${
              removeBorder ? 'disableBorderBotton' : 'enableBorderBottom'
            }`}
          >
            <MediumTypography label={appointment.modifier ?? '-'} />
          </TableCell>
          <TableCell
            className={`${
              removeBorder ? 'disableBorderBotton' : 'enableBorderBottom'
            }`}
          >
            {renderStatus(appointment.onHold ? 'On-hold' : appointment.status)}
          </TableCell>
          {parentArchived ? (
            <TableCell
              className={`${
                removeBorder ? 'disableBorderBotton' : 'enableBorderBottom'
              }`}
            />
          ) : (
            <TableCell
              sx={{
                paddingLeft: '4px',
              }}
              className={`${
                removeBorder ? 'disableBorderBotton' : 'enableBorderBottom'
              }`}
            >
              {appointment.archived ? (
                <Box
                  display={'inline-block'}
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    paddingLeft: '8px',
                    paddingRight: '8px',
                  }}
                  onClick={() => {
                    if (
                      appointment.archivalStatus !== 'Submitted' &&
                      archiveAppointment &&
                      appointment.progressNotesId !== null
                    ) {
                      archiveAppointment(
                        appointment.progressNotesId,
                        !appointment.archived,
                      );
                    }
                  }}
                >
                  <TextWithIcon
                    className="paddingZero archivedIconMinusRemoveSm"
                    LeftIcon={
                      appointment.archivalStatus !== 'Submitted' && <UndoIcon />
                    }
                    labelId={'archivedText'}
                    defaultLabel="Archived"
                    sxProps={{ color: '#97A6A5', textTransform: 'none' }}
                  />
                </Box>
              ) : (
                <Box
                  className="alignItemCenter"
                  sx={{
                    display: 'flex',
                  }}
                >
                  {checkPermissionForFeature(
                    'backend.progress_note',
                    'viewPermission',
                  ) && (
                    <ActionToolTip
                      label={`${rctl.formatMessage({
                        id: 'progressNoteText',
                        defaultMessage: 'Progress Note',
                      })}: ${appointment.progressNotesStatus}`}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          visibility:
                            appointment.attendeeRole !== 'CLINICIAN'
                              ? 'hidden'
                              : 'visible',
                          cursor: 'pointer',
                          marginLeft: '8px',
                        }}
                        onClick={() => {
                          const attendies: { id: string }[] = [];
                          const clientIds: string[] = [];

                          attendies.push({ id: appointment.attendeeId });
                          clientIds.push(appointment.clientId);

                          if (appointment.commonAppointmentId) {
                            navigate(PROGRESS_NOTE_SETUP, {
                              state: {
                                sortRequired: isEmployee,
                                eventId: appointment.commonAppointmentId,
                                attendeeId: attendies,
                                clientIds: clientIds,
                                screenFrom: screenName,
                                selectedDate: selectedDate,
                                status: parentStatus,
                              },
                            });
                          }
                        }}
                      >
                        <ProgressNote
                          stroke={getStatusColor(
                            appointment.progressNotesStatus,
                          )}
                        />
                      </Box>
                    </ActionToolTip>
                  )}
                  {appointment.visitId && (
                    <ActionToolTip labelId="BlockTime.note" defaultLabel="Note">
                      <Box
                        sx={{
                          padding: '4px',
                          display: 'flex',
                          cursor: 'pointer',
                          marginLeft: '8px',
                        }}
                        onClick={() => {
                          if (appointment.visitId) {
                            updateVisitNote(
                              appointment.progressNotesId,
                              appointment.visitId,
                              appointment.description,
                              appointment.status,
                            );
                          }
                        }}
                      >
                        <MessageIcon
                          stroke={
                            appointment.description &&
                            appointment.description.length > 0
                              ? '#00C6B8'
                              : '#97A6A5'
                          }
                        />
                      </Box>
                    </ActionToolTip>
                  )}

                  {(parentStatus === 'Not Submitted' ||
                    parentStatus === 'Rejected') &&
                    isEmployee && (
                      <ActionToolTip
                        labelId="archiveText"
                        defaultLabel="Archive"
                      >
                        <Box
                          sx={{
                            padding: '4px',
                            display: 'none',
                            cursor: 'pointer',
                            marginLeft: '8px',
                            opacity:
                              appointment.progressNotesStatus === 'Pending'
                                ? '0.5'
                                : 'unset',
                            pointerEvents:
                              appointment.progressNotesStatus === 'Pending'
                                ? 'none'
                                : 'auto',
                          }}
                          onClick={() => {
                            if (
                              archiveAppointment &&
                              appointment.progressNotesId !== null
                            ) {
                              if (
                                appointment.archivalStatus === null ||
                                appointment.archivalStatus === 'Pending'
                              ) {
                                archiveAppointment(
                                  appointment.progressNotesId,
                                  !appointment.archived,
                                );
                              }
                            }
                          }}
                        >
                          <ArchiveIcon />
                        </Box>
                      </ActionToolTip>
                    )}
                </Box>
              )}
            </TableCell>
          )}
          <TableCell
            sx={{
              backgroundColor: getBackgroundColor(),
            }}
            className={`disableBorderBotton`}
          />
        </TableRow>
      </>
    );
  };

  return (
    <Table
      className={`${
        groupAppointments && groupAppointments.length > 0
          ? ''
          : 'activitySheetNextedTable'
      } listDataTableHead`}
      sx={{
        marginLeft: '4px',
      }}
    >
      <TableHeader
        labelSxProp={{ whiteSpace: 'normal' }}
        headerNames={headCells}
        checkBoxRequired={false}
        className={'ActivityTableBorderHeader ' + getClassName()}
      />
      <TableBody className="activity-table-bg tableRowcss">
        {groupAppointments?.map((groupAppointment) => {
          return groupAppointment.appointments.map((appointment, index) => {
            let removeBorder = false;
            if (
              groupAppointment.isGroupBilling &&
              !groupAppointment.hasInterpreter &&
              groupAppointment.appointments.length - 1 !== index
            ) {
              removeBorder = true;
            }
            return getActivityTableRow(
              appointment,
              groupAppointment.isGroupBilling,
              index,
              removeBorder,
              groupAppointment.hasInterpreter,
            );
          });
        })}
      </TableBody>
    </Table>
  );
};

export default AppointmentTableRow;
